import { PathPickMarginFilterUnit } from "@/features/pathpick/types";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";

// 상세 필터의 범위 입력 상태 — 상품 찾기와 담은 상품이 같은 키로 쿼리스트링에 둔다.
// 마진은 기준(marginUnit)에 따라 같은 칸이 마진율(%) 또는 마진액(원)이 되므로 값 키를 하나로 두고,
// 요청 파라미터로 바꿀 때 기준에 맞는 이름으로 보낸다.
export interface PathPickRangeFilter {
  // 디폴트(마진율)는 쿼리스트링에서 생략한다.
  marginUnit?: PathPickMarginFilterUnit;
  kreamMarginMin?: number;
  kreamMarginMax?: number;
  poizonMarginMin?: number;
  poizonMarginMax?: number;
  kreamSalesMin?: number;
  kreamSalesMax?: number;
  poizonSalesMin?: number;
  poizonSalesMax?: number;
}

export type PathPickRangeFilterKey = keyof PathPickRangeFilter;

export type PathPickRangeField =
  | "kreamMargin"
  | "poizonMargin"
  | "kreamSales"
  | "poizonSales";

export interface PathPickRangeFieldDefinition {
  field: PathPickRangeField;
  kind: "margin" | "sales";
  minKey: PathPickRangeFilterKey;
  maxKey: PathPickRangeFilterKey;
}

// 화면 배치 순서(2×2: 크림 마진 / 포이즌 마진 / 크림 거래수 / 포이즌 거래수) 그대로 둔다.
export const PATH_PICK_RANGE_FIELDS: PathPickRangeFieldDefinition[] = [
  {
    field: "kreamMargin",
    kind: "margin",
    minKey: "kreamMarginMin",
    maxKey: "kreamMarginMax",
  },
  {
    field: "poizonMargin",
    kind: "margin",
    minKey: "poizonMarginMin",
    maxKey: "poizonMarginMax",
  },
  {
    field: "kreamSales",
    kind: "sales",
    minKey: "kreamSalesMin",
    maxKey: "kreamSalesMax",
  },
  {
    field: "poizonSales",
    kind: "sales",
    minKey: "poizonSalesMin",
    maxKey: "poizonSalesMax",
  },
];

export const DEFAULT_PATH_PICK_MARGIN_FILTER_UNIT: PathPickMarginFilterUnit =
  "rate";

const MARGIN_FILTER_UNITS: PathPickMarginFilterUnit[] = ["rate", "amount"];

// 전체 해제·탭 전환·비교 표시 OFF 때 함께 비우는 키.
export const PATH_PICK_RANGE_FILTER_KEYS: PathPickRangeFilterKey[] = [
  "marginUnit",
  ...PATH_PICK_RANGE_FIELDS.flatMap(({ minKey, maxKey }) => [minKey, maxKey]),
];

// 범위 값만 담긴 키(기준 제외) — 적용 여부 판단에 쓴다.
const RANGE_VALUE_KEYS = PATH_PICK_RANGE_FILTER_KEYS.filter(
  (key) => key !== "marginUnit"
);

// 마진 기준이 바뀌면 단위가 달라 기존 값이 무의미해지므로 함께 비우는 키.
export const PATH_PICK_MARGIN_RANGE_KEYS: PathPickRangeFilterKey[] =
  PATH_PICK_RANGE_FIELDS.filter(({ kind }) => kind === "margin").flatMap(
    ({ minKey, maxKey }) => [minKey, maxKey]
  );

// 모든 범위 키를 비운 값 — 전체 해제/탭 전환/비교 표시 OFF가 공유한다.
export const CLEARED_PATH_PICK_RANGE_FILTER =
  PATH_PICK_RANGE_FILTER_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: undefined }),
    {} as PathPickRangeFilter
  );

const toNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// 쿼리스트링에서 읽은 범위 값 정규화 — useTypedSearchParams는 키 접미사(id/count/amount 등)로만
// 숫자를 파싱해 이 키들은 문자열로 들어온다. 숫자가 아니거나 알 수 없는 기준이면 미적용으로 떨어뜨린다.
export const parsePathPickRangeFilter = (
  raw: PathPickRangeFilter
): PathPickRangeFilter => ({
  marginUnit:
    raw.marginUnit && MARGIN_FILTER_UNITS.includes(raw.marginUnit)
      ? raw.marginUnit
      : undefined,
  ...RANGE_VALUE_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: toNumber(raw[key]) }),
    {} as PathPickRangeFilter
  ),
});

export const getPathPickMarginFilterUnit = (range: PathPickRangeFilter) =>
  range.marginUnit ?? DEFAULT_PATH_PICK_MARGIN_FILTER_UNIT;

export const hasPathPickRangeFilter = (range: PathPickRangeFilter) =>
  RANGE_VALUE_KEYS.some((key) => range[key] !== undefined);

// 범위 상태 → 상품 조회 파라미터. 한쪽만 입력돼도 그 값만 보낸다(서버가 경계 포함으로 처리).
export const toPathPickRangeFilterParams = (
  range: PathPickRangeFilter
): PathPickProductRequestParams => {
  const marginParams: PathPickProductRequestParams =
    getPathPickMarginFilterUnit(range) === "rate"
      ? {
          kreamMarginRateMin: range.kreamMarginMin,
          kreamMarginRateMax: range.kreamMarginMax,
          poizonMarginRateMin: range.poizonMarginMin,
          poizonMarginRateMax: range.poizonMarginMax,
        }
      : {
          kreamMarginMin: range.kreamMarginMin,
          kreamMarginMax: range.kreamMarginMax,
          poizonMarginMin: range.poizonMarginMin,
          poizonMarginMax: range.poizonMarginMax,
        };

  return {
    ...marginParams,
    kreamSalesMin: range.kreamSalesMin,
    kreamSalesMax: range.kreamSalesMax,
    poizonSalesMin: range.poizonSalesMin,
    poizonSalesMax: range.poizonSalesMax,
  };
};

// 최소·최대를 둘 다 입력했는데 최소가 더 큰 항목 — 이 항목이 있으면 필터를 적용할 수 없다.
// 한쪽만 입력한 경우는 유효하다.
export const getInvalidPathPickRangeFields = (
  range: PathPickRangeFilter
): PathPickRangeField[] =>
  PATH_PICK_RANGE_FIELDS.filter(({ minKey, maxKey }) => {
    const min = range[minKey] as number | undefined;
    const max = range[maxKey] as number | undefined;
    return min !== undefined && max !== undefined && min > max;
  }).map(({ field }) => field);
